import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Users, 
  Edit, 
  Trash2, 
  Search,
  Shield,
  Trophy,
  Target,
  Save,
  ShieldPlus,
  ShieldMinus,
  Crown
} from 'lucide-react';
import { SponsorBanner } from '@/components/SponsorBanner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TGA_LOGO = "https://customer-assets.emergentagent.com/job_853d16bb-77c9-4e1d-85ff-db52f6af924e/artifacts/lsv7codb_Copy%20of%20TGA%20pro%20tour%20logo%20transparent.png";

export const AdminMembersPage = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', tournaments_played: 0, best_score: null });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!member?.is_admin) {
      navigate('/dashboard');
      return;
    }
    fetchMembers();
  }, [member, navigate]);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/members?admin_id=${member.id}`);
      setMembers(response.data);
    } catch (error) {
      toast.error('Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (m) => {
    setEditingMember(m);
    setEditForm({
      name: m.name,
      tournaments_played: m.tournaments_played || 0,
      best_score: m.best_score
    });
  };

  const handleSaveEdit = async () => {
    if (!editingMember) return;
    
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/members/${editingMember.id}?admin_id=${member.id}`,
        {
          name: editForm.name,
          tournaments_played: parseInt(editForm.tournaments_played) || 0,
          best_score: editForm.best_score !== null && editForm.best_score !== '' 
            ? parseInt(editForm.best_score) 
            : null
        }
      );
      toast.success('Member updated successfully');
      setEditingMember(null);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update member');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (m) => {
    if (!window.confirm(`Are you sure you want to delete ${m.name}? This will remove them from all tournaments.`)) return;
    
    try {
      await axios.delete(`${API_URL}/admin/members/${m.id}?admin_id=${member.id}`);
      toast.success('Member deleted successfully');
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete member');
    }
  };

  const handlePromote = async (m) => {
    if (!window.confirm(`Promote ${m.name} to admin?`)) return;
    
    try {
      await axios.post(`${API_URL}/admin/promote/${m.id}?admin_id=${member.id}`);
      toast.success(`${m.name} is now an admin`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote member');
    }
  };

  const handleDemote = async (m) => {
    if (!window.confirm(`Remove admin access from ${m.name}?`)) return;
    
    try {
      await axios.post(`${API_URL}/admin/demote/${m.id}?admin_id=${member.id}`);
      toast.success(`${m.name} is no longer an admin`);
      fetchMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to demote member');
    }
  };

  const formatScoreToPar = (score) => {
    if (score === null || score === undefined) return '--';
    if (score === 0) return 'E';
    if (score > 0) return `+${score}`;
    return score.toString();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.member_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!member?.is_admin) return null;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Sponsor Banner - Top of Page */}
      <SponsorBanner variant="top-banner" />

      {/* Navigation */}
      <nav className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button 
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-muted-foreground hover:text-white"
            data-testid="back-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
          <img 
            src={TGA_LOGO} 
            alt="TGA Pro Tour" 
            className="h-10 object-contain cursor-pointer"
            onClick={() => navigate('/dashboard')}
          />
          <div className="w-20" /> {/* Spacer for centering */}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-heading text-white flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              Manage Members
            </h1>
            <p className="text-muted-foreground mt-1">
              {members.length} registered members
            </p>
          </div>
          
          {/* Search */}
          <div className="relative w-full md:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name, email, or member #"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-muted border-border focus:border-primary"
              data-testid="member-search"
            />
          </div>
        </div>

        {/* Members List */}
        <Card className="bg-card border-white/10">
          <CardHeader>
            <CardTitle className="font-heading text-xl flex items-center gap-2">
              <Users className="w-5 h-5 text-primary" />
              All Members
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-primary">Loading members...</div>
              </div>
            ) : filteredMembers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">
                  {searchTerm ? 'No members found matching your search' : 'No members registered yet'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredMembers.map((m, index) => (
                  <div 
                    key={m.id}
                    className="flex flex-col md:flex-row md:items-center justify-between p-4 bg-muted rounded-lg gap-4 animate-fade-in"
                    style={{ animationDelay: `${index * 0.03}s` }}
                    data-testid={`member-row-${index}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-medium">{m.name}</span>
                        {m.is_super_admin && (
                          <span className="px-2 py-0.5 rounded bg-yellow-500/20 text-yellow-500 text-xs font-medium border border-yellow-500/50 flex items-center gap-1">
                            <Crown className="w-3 h-3" />
                            Super Admin
                          </span>
                        )}
                        {m.is_admin && !m.is_super_admin && (
                          <span className="px-2 py-0.5 rounded bg-primary/20 text-primary text-xs font-medium border border-primary/50">
                            Admin
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="font-mono text-primary">{m.member_number}</span>
                        <span>{m.email}</span>
                        <span>Joined {formatDate(m.created_at)}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <div className="flex gap-4 text-sm">
                        <div className="text-center">
                          <Trophy className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                          <span className="text-white font-mono">{m.tournaments_played || 0}</span>
                        </div>
                        <div className="text-center">
                          <Target className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
                          <span className={`font-mono ${
                            m.best_score !== null && m.best_score < 0 ? 'text-secondary' :
                            m.best_score === 0 ? 'text-primary' : 'text-white'
                          }`}>
                            {formatScoreToPar(m.best_score)}
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex gap-2">
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleEdit(m)}
                              className="border-primary/50 hover:bg-primary/20"
                              data-testid={`edit-member-${m.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="bg-card border-white/10">
                            <DialogHeader>
                              <DialogTitle className="font-heading">Edit Member</DialogTitle>
                              <DialogDescription>
                                Update details for {editingMember?.name}
                              </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4 my-4">
                              <div>
                                <label className="text-sm text-muted-foreground mb-1 block">Name</label>
                                <Input
                                  value={editForm.name}
                                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                  className="bg-muted border-border"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-muted-foreground mb-1 block">Tournaments Played</label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={editForm.tournaments_played}
                                  onChange={(e) => setEditForm({ ...editForm, tournaments_played: e.target.value })}
                                  className="bg-muted border-border"
                                />
                              </div>
                              <div>
                                <label className="text-sm text-muted-foreground mb-1 block">Best Score (relative to par)</label>
                                <Input
                                  type="number"
                                  placeholder="e.g., -2, 0, 3"
                                  value={editForm.best_score ?? ''}
                                  onChange={(e) => setEditForm({ ...editForm, best_score: e.target.value })}
                                  className="bg-muted border-border"
                                />
                              </div>
                            </div>
                            
                            <DialogFooter>
                              <DialogClose asChild>
                                <Button variant="outline">Cancel</Button>
                              </DialogClose>
                              <Button 
                                onClick={handleSaveEdit}
                                disabled={saving}
                                className="bg-primary hover:bg-primary/90"
                              >
                                <Save className="w-4 h-4 mr-1" />
                                {saving ? 'Saving...' : 'Save Changes'}
                              </Button>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>
                        
                        {/* Promote/Demote buttons - only for super admins */}
                        {member?.is_super_admin && !m.is_super_admin && (
                          <>
                            {!m.is_admin ? (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handlePromote(m)}
                                className="border-green-500/50 hover:bg-green-500/20 text-green-500"
                                data-testid={`promote-member-${m.id}`}
                                title="Promote to Admin"
                              >
                                <ShieldPlus className="w-4 h-4" />
                              </Button>
                            ) : (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDemote(m)}
                                className="border-orange-500/50 hover:bg-orange-500/20 text-orange-500"
                                data-testid={`demote-member-${m.id}`}
                                title="Remove Admin Access"
                              >
                                <ShieldMinus className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                        
                        {!m.is_admin && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(m)}
                            className="border-destructive/50 hover:bg-destructive/20 text-destructive"
                            data-testid={`delete-member-${m.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
